import { zip } from 'fflate'
import type { PhotoRecord } from '~/models/photo'
import { EXPORT_FORMAT_VERSION } from '~/models/schemaVersion'
import { mediaFileNameFor } from '../json/weeklyReport'
import type { ReportModel } from '../reportModel'

/**
 * The optional selected-media ZIP (D-082).
 *
 *   sola-media-YYYYMMDD.zip
 *   ├─ manifest.json
 *   └─ photos/<photoId>-<view>.jpg
 *
 * **Separate from the report, and separate from a backup.** A backup always
 * contains everything and has no privacy toggles; this contains only what the
 * user explicitly selected for one export, and exists at all only because the
 * PDF is restricted to compressed thumbnails and someone may want the originals
 * alongside it.
 *
 * **The manifest is the whole point.** Without it, `photos/` is a pile of
 * anonymous JPEGs; with it, a reader can say which file is the front view from
 * week three. The file names match `mediaFile` in the JSON document, so the two
 * artifacts join up without either being built first.
 */

export const MEDIA_FORMAT = 'project-sola-media'
export const MEDIA_MANIFEST_FILE = 'manifest.json'

export interface MediaManifestEntry {
  readonly path: string
  readonly view: string
  /**
   * Days from the first observation in the report, always present.
   *
   * Ordering and gaps survive redaction through `dayIndex` rather than through a
   * calendar date (D-060), so a reader can sequence the photos even when exact
   * dates were excluded.
   */
  readonly dayIndex: number
  /** Present only when the user included exact dates. */
  readonly capturedDate?: string
  readonly source: string
  readonly widthPx: number
  readonly heightPx: number
  readonly byteSize: number
}

export interface MediaManifest {
  readonly format: typeof MEDIA_FORMAT
  readonly formatVersion: number
  readonly generatedAt: string
  /** Restated here so the ZIP explains itself without the report beside it. */
  readonly privacySelection: {
    readonly scope: string
    readonly includePhotos: boolean
    readonly includeExactDates: boolean
  }
  readonly photoCount: number
  readonly entries: readonly MediaManifestEntry[]
  /** Stated in the artifact, not only in the app that made it. */
  readonly notice: string
}

export interface MediaSource {
  readonly photo: PhotoRecord
  /** The stored image, not the thumbnail. The PDF gets the thumbnail. */
  readonly bytes: Uint8Array
  readonly dayIndex: number
}

export interface MediaZipFile {
  readonly fileName: string
  readonly bytes: Uint8Array
  readonly manifest: MediaManifest
}

export function buildMediaManifest(
  model: ReportModel,
  sources: readonly MediaSource[],
): MediaManifest {
  const { selection } = model

  return {
    format: MEDIA_FORMAT,
    formatVersion: EXPORT_FORMAT_VERSION,
    generatedAt: model.generatedAt as string,
    privacySelection: {
      scope: selection.scope,
      includePhotos: selection.includePhotos,
      includeExactDates: selection.includeExactDates,
    },
    photoCount: sources.length,
    entries: sources.map(({ photo, bytes, dayIndex }) => ({
      path: mediaFileNameFor(photo),
      view: photo.view,
      dayIndex,
      /* Exact dates are gated here exactly as they are in the report. A
         manifest that restated a date the document had redacted would be the
         export-gating rule silently narrowed by a new artifact — which is the
         mistake every previous checkpoint's notes warn about. */
      ...(selection.includeExactDates ? { capturedDate: photo.capturedDate as string } : {}),
      source: photo.source,
      widthPx: photo.widthPx,
      heightPx: photo.heightPx,
      byteSize: bytes.length,
    })),
    notice:
      'These are your original photos. Once this file leaves Project Sola it cannot be recalled, ' +
      'deleted, or protected.',
  }
}

export async function buildMediaZip(
  model: ReportModel,
  sources: readonly MediaSource[],
): Promise<MediaZipFile> {
  const manifest = buildMediaManifest(model, sources)

  const entries: Record<string, [Uint8Array, { level: 0 | 6 }]> = {
    [MEDIA_MANIFEST_FILE]: [
      new TextEncoder().encode(JSON.stringify(manifest, null, 2)),
      { level: 6 },
    ],
  }

  for (const source of sources) {
    /* Level 0. A JPEG is already compressed; deflating it again costs time and
       memory on a phone and saves nothing — the same reasoning the backup
       container applies to blobs. */
    entries[mediaFileNameFor(source.photo)] = [source.bytes, { level: 0 }]
  }

  return { fileName: `sola-media-${model.reference}.zip`, bytes: await zipAsync(entries), manifest }
}

/** Promise wrapper over fflate's async zip, which uses a worker when available. */
function zipAsync(files: Record<string, [Uint8Array, { level: 0 | 6 }]>): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    zip(files, (error, data) => (error ? reject(error) : resolve(data)))
  })
}
